import { Head, Link, usePage } from "@inertiajs/react"
import { Check, Copy, ExternalLink } from "lucide-react"
import { useState } from "react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import type { PageProps } from "@/types/inertia"

type Props = PageProps & {
  room_id: string
  expires_at: string
  client_url: string
  host_url: string
}

const TITLE = "Session room ready"
const DESCRIPTION = "Host and client links for a Sanctuary video session."

function LinkBox({
  label,
  hint,
  url,
  tone,
}: {
  label: string
  hint: string
  url: string
  tone: "accent" | "neutral"
}) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      window.prompt("Copy this link:", url)
    }
  }

  return (
    <div className="callout">
      <div className="flex items-center justify-between gap-3">
        <h2>{label}</h2>
        <Badge tone={tone}>{tone === "accent" ? "Keep private" : "Send to client"}</Badge>
      </div>
      <p className="mt-1 text-ink-muted">{hint}</p>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <code className="form-control min-w-0 flex-1 overflow-x-auto whitespace-nowrap text-xs">{url}</code>
        <Button variant="secondary" onClick={copy} aria-live="polite">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span className="ml-2">{copied ? "Copied" : "Copy"}</span>
        </Button>
      </div>
    </div>
  )
}

export default function AdminVideoSessionsShow() {
  const { room_id, expires_at, client_url, host_url } = usePage<Props>().props
  const expires = new Date(expires_at)

  return (
    <AdminShell>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <PageHeader
        title={TITLE}
        description={
          <>
            Room <code>{room_id}</code> · expires{" "}
            {expires.toLocaleString("en-GB", { weekday: "short", hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}
          </>
        }
        actions={
          <>
            <Button variant="secondary" asChild>
              <Link href="/admin/video-sessions/new">New room</Link>
            </Button>
            <Button asChild>
              <a href={host_url} target="_blank" rel="noopener">
                <ExternalLink className="h-4 w-4" />
                <span className="ml-2">Open your host link</span>
              </a>
            </Button>
          </>
        }
      />

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <LinkBox
          label="Client link"
          hint="Send this to your client. They will wait in a waiting room until you admit them."
          url={client_url}
          tone="neutral"
        />
        <LinkBox
          label="Your host link"
          hint="Open this yourself at session time. Anyone holding it can host the session, so keep it private."
          url={host_url}
          tone="accent"
        />
      </div>

      <div className="callout callout-signal mt-6">
        Links contain a random room code and no client information. Never add a client's name to a link,
        email subject, or calendar entry alongside it. This page is not saved anywhere — if you leave it,
        create a new room.
      </div>
    </AdminShell>
  )
}
